/**
 * Per-field validation messages for the card form, ported from DealerFlow 360's FieldError
 * (dealerflow360/web/src/shared/components/FieldError/FieldError.tsx) without its message component: the LOS shows
 * each message with Kendo's `Error` from @progress/kendo-react-labels.
 *
 * Messages are gated on touched: the checks run against the live values, so an ungated form would open with a red
 * line under every empty field. A field becomes touched when it is edited, and every field when the form is sent
 * (`revealErrors`), which is why the send button stays enabled on an invalid form: it reveals the reasons in place.
 */

import { useCallback, useId, useState } from 'react';

/** Validation messages by field name. An empty object means valid. */
export type FieldErrors<T> = Partial<Record<keyof T & string, string>>;

const NO_FIELDS: ReadonlySet<string> = new Set();

/** The message for a field, and the id its control points at. */
export interface FieldErrorProps {
  id: string;
  message?: string;
}

/** Spread onto the Kendo control the message describes. Empty while the field is valid or untouched. */
export interface FieldControlProps {
  ariaDescribedBy?: string;
  valid?: boolean;
}

export interface FieldErrorState<T> {
  errorProps: (field: keyof T & string) => FieldErrorProps;
  controlProps: (field: keyof T & string) => FieldControlProps;
  /** Records fields as edited. */
  markTouched: (fields: string[]) => void;
  /** Back to pristine. */
  resetTouched: () => void;
  /** Reveals every outstanding message; true when there was none. */
  revealErrors: () => boolean;
}

export function useFieldErrors<T>(errors: FieldErrors<T>): FieldErrorState<T> {
  const [touched, setTouched] = useState<ReadonlySet<string>>(NO_FIELDS);
  // Per form instance, so two mounted forms never share a message id.
  const baseId = useId();

  const markTouched = useCallback((fields: string[]) => {
    setTouched((previous) => {
      if (fields.every((field) => previous.has(field))) return previous;
      return new Set([...previous, ...fields]);
    });
  }, []);

  const resetTouched = useCallback(() => setTouched(NO_FIELDS), []);

  const messageFor = (field: keyof T & string) => (touched.has(field) ? errors[field] : undefined);
  const errorId = (field: keyof T & string) => `${baseId}${field}-error`;

  return {
    errorProps: (field) => ({ id: errorId(field), message: messageFor(field) }),
    controlProps: (field) => (messageFor(field) ? { ariaDescribedBy: errorId(field), valid: false } : {}),
    markTouched,
    resetTouched,
    revealErrors: () => {
      if (Object.keys(errors).length === 0) return true;
      setTouched((previous) => new Set([...previous, ...Object.keys(errors)]));
      return false;
    },
  };
}
